import React, { Fragment, useState } from 'react';
import classNames from 'classnames';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import uniqid from 'uniqid';
import Icon from 'src/components/ui/Icon/Icon';
import NavItem, { NavItemProps } from '../NavItem/NavItem';
import styles from './NavItemList.module.scss';
import { useUserAccessRightsQuery } from '../../../../services/user';

const COLLAPSED_SECTIONS_KEY = 'navCollapsedSections';

const readCollapsedSections = (): string[] => {
  try {
    const stored = JSON.parse(localStorage.getItem(COLLAPSED_SECTIONS_KEY) || '[]');
    return Array.isArray(stored) ? stored : [];
  } catch {
    return [];
  }
};

export type NavItemListProps = {
  items: NavItemProps[];
  expanded?: boolean;
};

const NavItemList: React.FC<NavItemListProps> = ({ items, expanded }) => {
  const location = useLocation();
  const { t } = useTranslation();
  const { data: accessRights, isLoading: isAccessLoading } = useUserAccessRightsQuery();
  const [collapsedSections, setCollapsedSections] = useState<string[]>(readCollapsedSections);

  const toggleSection = (section: string) => {
    setCollapsedSections(prev => {
      const next = prev.includes(section) ? prev.filter(s => s !== section) : [...prev, section];
      localStorage.setItem(COLLAPSED_SECTIONS_KEY, JSON.stringify(next));
      return next;
    });
  };
  const hasAccess = (link?: string): boolean => {
    if (!link) return false;
    if (link.startsWith('dashboard')) return true;
    if (isAccessLoading || !accessRights) return false;
    const matchedRight = accessRights.find(right => right.route === `/${link}`);
    return matchedRight?.read || matchedRight?.read_write || false;
  };

  const getBetaFlag = (link?: string): boolean => {
    if (!link || !accessRights) return false;

    const matchedRight = accessRights.find(right => right.route === `/${link}`);

    return matchedRight?.is_beta ?? false;
  };

  const filteredItems = items
    .filter(item => hasAccess(item.link as string))
    .map(item => {
      const filteredChilds =
        item.childs
          ?.filter(child => hasAccess(child.link as string))
          .map(child => ({
            ...child,
            is_beta: getBetaFlag(child.link as string),
          })) || [];

      return {
        ...item,
        is_beta: getBetaFlag(item.link as string),
        childs: filteredChilds,
      };
    });

  return (
    <ul className={styles.root}>
      {filteredItems.map((item, index) => {
        const currentPath = location.pathname.split('/')[1];
        const isActive = (item.link as string).includes(currentPath) && currentPath !== '';
        const startsNewSection = Boolean(item.section) && item.section !== filteredItems[index - 1]?.section;
        const isSectionCollapsed = Boolean(item.section) && collapsedSections.includes(item.section as string);

        return (
          <Fragment key={uniqid(item.label)}>
            {startsNewSection &&
              (expanded ? (
                <li className={index === 0 ? 'pt-8' : 'pt-20'}>
                  <button
                    type="button"
                    onClick={() => toggleSection(item.section as string)}
                    aria-expanded={!isSectionCollapsed}
                    className="w-full flex items-center justify-between px-12 pb-4 typo-text-12--bold text-sidebar-muted uppercase tracking-wider hover:text-full--dark">
                    {t(item.section as string)}
                    <Icon
                      name="chevronDown"
                      width={16}
                      height={16}
                      className={classNames('transition-transform duration-200', { '-rotate-90': isSectionCollapsed })}
                    />
                  </button>
                </li>
              ) : (
                <li className={classNames('mx-4', index === 0 ? 'mb-8' : 'my-8')}>
                  <button
                    type="button"
                    onClick={() => toggleSection(item.section as string)}
                    aria-expanded={!isSectionCollapsed}
                    aria-label={t(item.section as string)}
                    title={t(item.section as string)}
                    className="w-full flex items-center gap-4 text-sidebar-muted hover:text-full--dark">
                    <span className="flex-1 border-subtle--dark border-t-1" aria-hidden />
                    <Icon
                      name="chevronDown"
                      width={12}
                      height={12}
                      className={classNames('transition-transform duration-200', { '-rotate-90': isSectionCollapsed })}
                    />
                    <span className="flex-1 border-subtle--dark border-t-1" aria-hidden />
                  </button>
                </li>
              ))}
            {!isSectionCollapsed && (
              <>
                <li className="mb-2 last:mb-0">
                  <NavItem {...item} expanded={expanded} />
                </li>
                {isActive && item.childs && item.childs.length > 0 && (
                  <div className={styles.childMenu}>
                    <NavItemList items={item.childs} expanded={expanded} />
                  </div>
                )}
              </>
            )}
          </Fragment>
        );
      })}
    </ul>
  );
};

export default NavItemList;
